import { CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import Icon from '../../../../../atoms/Icon'
import SydButton from '../../../../../commonDesign/Button'
import { useCheckPolicy, useDebouncedCallback } from '../../../../../../hooks'
import AbundanceDesigner from './AbundanceDesigner'
import ContentCodeView from './ContentCodeView'
import ContentPreviewView from './ContentPreviewView'

const ViewContentContainer = forwardRef(({
  jsxContent,
  viewId,
  onJsxContentChange
}, ref) => {
  const codeViewRef = useRef(null)

  // Expose method to get current editor value
  useImperativeHandle(ref, () => ({
    getEditorValue: () => codeViewRef.current?.getValue?.()
  }))

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode)
    // Focus the editor when switching to code view
    if (mode === 'code') {
      setTimeout(() => {
        codeViewRef.current?.focus?.()
      }, 100)
    }
  }, [])

  const views = [
    {
      id: 'code',
      title: 'Code View',
      icon: 'file',
      size: 'sm'
    }, {
      id: 'preview',
      title: 'Preview',
      icon: 'visibility',
      size: 'sm'
    }
  ]
  const [viewMode, setViewMode] = useState('code')
  const [aiDesignerActive, setAiDesignerActive] = useState(false)
  const [aiGeneratedContent, setAiGeneratedContent] = useState(null)
  const [previousContent, setPreviousContent] = useState(null)
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false)
  const [persistedThreadId, setPersistedThreadId] = useState(null)
  const [previewContent, setPreviewContent] = useState(jsxContent)

  const userCanAccessDenaliLabs = useCheckPolicy('denali_labs')

  const debouncedFormUpdate = useDebouncedCallback(
    useCallback((jsx) => {
      onJsxContentChange(jsx)
    }, [onJsxContentChange]),
    500
  )

  const handleJsxContentChange = useCallback((jsx) => {
    // If user manually edits and content differs from AI-generated, clear AI state
    if (jsx !== aiGeneratedContent) {
      setAiGeneratedContent(null)
      setPreviousContent(null)
    }
    // Update preview content immediately (no debounce for preview)
    setPreviewContent(jsx)
    debouncedFormUpdate(jsx)
  }, [aiGeneratedContent, debouncedFormUpdate])

  const handleJsxUpdate = (jsx, isFromAttachment = false) => {
    if (onJsxContentChange) {
      onJsxContentChange(jsx)
      setPreviewContent(jsx)
      setViewMode('code')
      setTimeout(() => {
        setViewMode('preview')
      }, 100)
    }

    if (isFromAttachment) {
      if (jsxContent && jsxContent !== jsx) {
        setPreviousContent(jsxContent)
      }
      setAiGeneratedContent(jsx)
      setIsLoadingAttachment(false)
    }
  }

  const handleRevertAiContent = useCallback(() => {
    setAiGeneratedContent(null)
    setPreviousContent(null)
    setPreviewContent(previousContent)
    onJsxContentChange(previousContent)
    setViewMode('code')
    setTimeout(() => {
      setViewMode('preview')
    }, 100)
  }, [onJsxContentChange, previousContent])

  return (
    <div className='h-full w-full flex flex-col gap-5 relative'>
      <div className='flex gap-2 justify-between items-center'>
        {userCanAccessDenaliLabs ? (
          <div>
            <SydButton
              variant='ai'
              icon='sparkles'
              onClick={() => setAiDesignerActive(true)}
            >
              Abundance Designer
            </SydButton>
          </div>
        ) : null}
        <div className='flex gap-2'>
          {views.map((view) => (
            <SydButton
              key={view.id}
              {...view}
              variant={viewMode === view.id ? 'primary' : 'secondary'}
              onClick={() => handleViewModeChange(view.id)}
            >{view.title}
            </SydButton>
          ))}
        </div>
      </div>

      <div
        className='relative flex-1 flex flex-col'
        style={{
          overflow: 'hidden',
          border: '5px solid var(--color-neutral-300)',
          borderRadius: 'var(--border-radius-lg)',
          width: aiDesignerActive ? 'calc(100% - 410px)' : '100%',
          transition: 'width 0.3s ease-in-out'
        }}
      >
        <div className='relative h-full w-full' style={{ display: viewMode === 'code' ? 'block' : 'none' }}>
          {aiGeneratedContent === jsxContent && jsxContent && (
            <div
              className='absolute flex gap-2 items-center text-xs rounded p-1'
              style={{
                backgroundColor: 'var(--color-ai-100)',
                color: 'var(--color-ai-700)',
                border: '1px solid var(--color-ai-300)',
                zIndex: 99999,
                bottom: '10px',
                right: '10px',
                opacity: 0.9
              }}
            >
              <div className='px-2 py-1 flex gap-2 items-center'>
                <Icon name='sparkles' />
                AI Generated
              </div>
              {previousContent && (
                <SydButton
                  icon='history'
                  size='sm'
                  variant='ai'
                  onClick={handleRevertAiContent}
                >
                  Revert
                </SydButton>
              )}
            </div>
          )}
          <ContentCodeView
            ref={codeViewRef}
            jsxContent={jsxContent}
            onJsxContentChange={handleJsxContentChange}
          />
          {isLoadingAttachment && (
            <div
              className='absolute flex items-center justify-center'
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}
            >
              <div
                className='flex items-center gap-2 px-4 py-2 rounded'
                style={{
                  backgroundColor: 'var(--color-ai-100)',
                  color: 'var(--color-ai-700)',
                  border: '1px solid var(--color-ai-300)'
                }}
              >
                <CircularProgress color='inherit' size='18px' />
                Loading attachment...
              </div>
            </div>
          )}
        </div>

        <div className='relative h-full w-full' style={{ display: viewMode === 'preview' ? 'block' : 'none', overflow: 'auto' }}>
          {aiGeneratedContent === jsxContent && jsxContent && (
            <div
              className='absolute flex gap-2 items-center text-xs rounded p-1'
              style={{
                backgroundColor: 'var(--color-ai-100)',
                color: 'var(--color-ai-700)',
                border: '1px solid var(--color-ai-300)',
                zIndex: 99999,
                bottom: '10px',
                right: '10px',
                opacity: 0.9
              }}
            >
              <div className='px-2 py-1 flex gap-2 items-center'>
                <Icon name='sparkles' />
                AI Generated
              </div>
              {previousContent && (
                <SydButton
                  icon='history'
                  size='sm'
                  variant='ai'
                  onClick={handleRevertAiContent}
                >
                  Revert
                </SydButton>
              )}
            </div>
          )}
          {viewMode === 'preview' && <ContentPreviewView jsxContent={previewContent} />}
          {isLoadingAttachment && (
            <div
              className='absolute flex items-center justify-center'
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}
            >
              <div
                className='flex items-center gap-2 px-4 py-2 rounded'
                style={{
                  backgroundColor: 'var(--color-ai-100)',
                  color: 'var(--color-ai-700)',
                  border: '1px solid var(--color-ai-300)'
                }}
              >
                <CircularProgress color='inherit' size='18px' />
                Loading attachment...
              </div>
            </div>
          )}
        </div>
      </div>

      {aiDesignerActive && (
        <AbundanceDesigner
          onClose={() => setAiDesignerActive(false)}
          onJsxUpdate={handleJsxUpdate}
          onAttachmentLoadStart={() => setIsLoadingAttachment(true)}
          onThreadIdUpdate={setPersistedThreadId}
          currentJsxContent={jsxContent}
          viewId={viewId}
          initialThreadId={persistedThreadId}
        />
      )}
    </div>
  )
})

ViewContentContainer.displayName = 'ViewContentContainer'

ViewContentContainer.propTypes = {
  jsxContent: PropTypes.string,
  viewId: PropTypes.number,
  onJsxContentChange: PropTypes.func
}

export default ViewContentContainer
